<script setup lang="ts">
import type { SaleAssetBusEntity } from '~/apiClient';
import type { SaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: SaleItemEntity;
    asset: SaleAssetBusEntity;
}
const { asset } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { trackGalleryView } = useTracking();

// DATA
const activeIndex = ref<number>(0);
const lightboxOpen = ref<boolean>(false);
const baseImages = computed<string[]>(() => asset.images.base);
const thumbs = computed<string[]>(() => asset.images.base.slice(0, 4));
const { saleImageAlt } = useA11y();

// METHODS
const openLightbox = (): void => {
    lightboxOpen.value = true;
    trackGalleryView('photo', 'main');
};
</script>

<template>
    <div class="grid gap-6 md:grid-cols-[1fr_120px] md:gap-8">
        <NuxtImg
            v-if="baseImages.length"
            class="col-start-1 row-start-1 aspect-4/3 h-full w-full rounded-xl border object-cover"
            :src="baseImages.at(activeIndex)"
            :width="800"
            densities="x1"
            loading="lazy"
            :draggable="false"
            :alt="saleImageAlt(asset, baseImages.at(activeIndex))"
        />
        <SdpLightboxButton v-if="baseImages.length" class="col-start-1 row-start-1 m-3 self-start justify-self-end" @click="openLightbox" />
        <SaleTrustReportInspectionBadge class="col-start-1 row-start-1 m-3 self-end justify-self-start" :sale="sale" />
        <div class="grid grid-cols-3 gap-3 md:grid-cols-1">
            <template v-if="baseImages.length">
                <NuxtImg
                    v-for="(image, index) in thumbs"
                    :key="image"
                    class="m-auto aspect-4/3 cursor-pointer rounded-xl border-2 object-cover"
                    :class="{
                        'border-primary-950': index === activeIndex,
                        'col-start-3 row-start-1 md:col-start-1 md:row-start-3': index === 2,
                        'col-start-1 row-start-4 hidden md:block': index === 3,
                    }"
                    :src="image"
                    :width="240"
                    densities="x1"
                    loading="lazy"
                    :draggable="false"
                    :alt="saleImageAlt(asset, image)"
                    @click="activeIndex = index"
                />

                <UButton
                    class="col-start-3 row-start-1 m-auto w-full bg-neutral-900/50 font-bold hover:bg-neutral-800/50 md:col-start-1 md:row-start-4"
                    :ui="{
                        base: 'flex-col justify-evenly aspect-4/3 rounded-xl',
                        label: 'text-center break-normal line-clamp-2 whitespace-normal',
                    }"
                    color="neutral"
                    :label="tt('sdp.imageGallery', { totalImages: baseImages.length })"
                    icon="lucide:layout-grid"
                    @click="openLightbox"
                />
            </template>
        </div>

        <LazySdpLightboxImage v-if="baseImages.length" v-model="lightboxOpen" :items="baseImages" :initial-slide="activeIndex" />
    </div>
</template>
